import { useQuery, type QueryClient } from '@tanstack/react-query';
import { BaseApiService } from '@doccareplus/api-client';
import { selectActiveContext, useAppSelector } from '@doccareplus/kernel/application';
import type { PrescriptionLayoutResponse } from '@doccareplus/ui/prescription';

/**
 * The clinic's prescription letterhead/branding and effective prescription format.
 *
 * Lives in `shared` because two features read it: `visits` (authoring, the finalized record, the prescription desk
 * print view) and `settings` (the branding/format editor's live preview). Features must not import from each other.
 *
 * Carries no clinical data, so any active clinic member may read it.
 */
class PrescriptionLayoutApi extends BaseApiService {
  constructor() {
    super('/clinic');
  }

  getLayout(): Promise<PrescriptionLayoutResponse> {
    return this.get<PrescriptionLayoutResponse>('/visits/prescription-layout');
  }
}

const prescriptionLayoutApi = new PrescriptionLayoutApi();

export const prescriptionLayoutKeys = {
  all: ['prescription-layout'] as const,
  layout: (clinicId: string | undefined) => ['prescription-layout', clinicId] as const,
};

/**
 * Clinic-wide and rarely changing, so it is cached across the session and revalidated on mount rather than being
 * refetched per visit. Saving clinic settings invalidates it.
 */
export function usePrescriptionLayoutQuery() {
  const clinicId = useAppSelector(selectActiveContext)?.clinicId;

  return useQuery<PrescriptionLayoutResponse>({
    queryKey: prescriptionLayoutKeys.layout(clinicId),
    queryFn: () => prescriptionLayoutApi.getLayout(),
    enabled: Boolean(clinicId),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Warms the layout cache for a just-activated clinic/provider context (e.g. right after a visit draft is
 * created) so the workspace opens with the format already resolved instead of a per-component loading flicker.
 */
export function prefetchPrescriptionLayout(
  queryClient: QueryClient,
  clinicId: string | undefined,
): Promise<void> {
  if (!clinicId) {
    return Promise.resolve();
  }

  return queryClient.prefetchQuery({
    queryKey: prescriptionLayoutKeys.layout(clinicId),
    queryFn: () => prescriptionLayoutApi.getLayout(),
    staleTime: 5 * 60 * 1000,
  });
}
